import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Bold, Highlighter, List, Type } from 'lucide-react';
import { notePlainLength, sanitizeHtml } from '../../utils/sanitizeHtml';
import './NotesModal.scss';

const NOTE_HIGHLIGHT = '#fde68a';

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function editorTextLength(el: HTMLDivElement): number {
  return (el.innerText ?? '').replace(/\r/g, '').length;
}

function writeNotes(el: HTMLDivElement, notes: string, maxLength?: number) {
  if (looksLikeHtml(notes)) {
    el.innerHTML = sanitizeHtml(notes || '');
  } else {
    el.innerText = notes || '';
  }
  if (maxLength && editorTextLength(el) > maxLength) {
    el.innerText = (el.innerText ?? '').slice(0, maxLength);
  }
}

export interface NoteEditorHandle {
  getHtml: () => string;
  reset: (notes: string) => void;
}

export interface NoteEditorProps {
  notes: string;
  placeholder?: string;
  maxLength?: number;
  compact?: boolean;
  onBlurSave?: (html: string) => void | Promise<unknown>;
}

const NoteEditor = React.forwardRef<NoteEditorHandle, NoteEditorProps>(function NoteEditor(
  { notes, placeholder, maxLength, compact, onBlurSave },
  ref,
) {
  const { formatMessage: t } = useIntl();
  const editorRef = useRef<HTMLDivElement>(null);
  const [atLimit, setAtLimit] = useState(false);
  const [formats, setFormats] = useState({
    bold: false,
    italic: false,
    highlight: false,
    list: false,
    font: '3',
  });

  const readHtml = () => {
    let html = sanitizeHtml(editorRef.current?.innerHTML ?? '');
    if (maxLength && notePlainLength(html) > maxLength) {
      const el = editorRef.current;
      if (el) el.innerText = (el.innerText ?? '').slice(0, maxLength);
      html = sanitizeHtml(editorRef.current?.innerHTML ?? '');
    }
    return html;
  };

  useImperativeHandle(ref, () => ({
    getHtml: readHtml,
    reset: (value: string) => {
      const el = editorRef.current;
      if (!el) return;
      writeNotes(el, value, maxLength);
      setAtLimit(Boolean(maxLength && editorTextLength(el) >= maxLength));
    },
  }));

  const refreshFormats = () => {
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      highlight: /^(rgb\(253,\s*230,\s*138\)|#fde68a)/i.test(document.queryCommandValue('hiliteColor') || document.queryCommandValue('backColor') || ''),
      list: document.queryCommandState('insertUnorderedList'),
      font: document.queryCommandValue('fontSize') || '3',
    });
  };

  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;
    writeNotes(el, notes, maxLength);
    setAtLimit(Boolean(maxLength && editorTextLength(el) >= maxLength));
  }, [notes, maxLength]);

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    refreshFormats();
    const el = editorRef.current;
    setAtLimit(Boolean(maxLength && el && editorTextLength(el) >= maxLength));
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (!maxLength) return;
    const el = editorRef.current;
    if (!el) return;
    const input = e.nativeEvent as InputEvent;
    if (input.inputType?.startsWith('delete') || input.inputType === 'historyUndo') return;
    const incoming = input.data ?? '';
    if (!incoming) return;
    const selected = window.getSelection()?.toString().length ?? 0;
    const next = editorTextLength(el) - selected + incoming.length;
    if (next > maxLength) {
      e.preventDefault();
      setAtLimit(true);
    }
  };

  const handleInput = () => {
    const el = editorRef.current;
    if (!el || !maxLength) return;
    const text = el.innerText ?? '';
    if (text.length > maxLength) {
      el.innerText = text.slice(0, maxLength);
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    setAtLimit(editorTextLength(el) >= maxLength);
  };

  return (
    <div className={`notes-modal${compact ? ' notes-modal--inline' : ''}`}>
      <div className="notes-modal__toolbar" role="toolbar">
        <button type="button" className={`notes-modal__tool${formats.bold ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} title={t({ id: 'notes.toolbar.bold' })}>
          <Bold size={13} />
        </button>
        <button type="button" className={`notes-modal__tool${formats.italic ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} title={t({ id: 'notes.toolbar.italic' })}>
          <Type size={13} />
        </button>
        <button
          type="button"
          className={`notes-modal__tool${formats.highlight ? ' notes-modal__tool--active' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); exec('hiliteColor', NOTE_HIGHLIGHT); }}
          title={t({ id: 'notes.toolbar.highlight' })}
        >
          <Highlighter size={13} />
        </button>
        <button type="button" className={`notes-modal__tool${formats.list ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} title={t({ id: 'notes.toolbar.bullets' })}>
          <List size={13} />
        </button>
        <span className="notes-modal__toolbar-sep" />
        <button type="button" className={`notes-modal__tool${formats.font === '2' ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('fontSize', '2'); }} title={t({ id: 'notes.toolbar.fontSmall' })}>
          A−
        </button>
        <button type="button" className={`notes-modal__tool${formats.font === '3' ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('fontSize', '3'); }} title={t({ id: 'notes.toolbar.fontNormal' })}>
          A
        </button>
        <button type="button" className={`notes-modal__tool${formats.font === '4' ? ' notes-modal__tool--active' : ''}`} onMouseDown={(e) => { e.preventDefault(); exec('fontSize', '4'); }} title={t({ id: 'notes.toolbar.fontLarge' })}>
          A+
        </button>
      </div>
      <div
        ref={editorRef}
        className={`notes-modal__editor note-html${compact ? ' notes-modal__editor--inline' : ''}`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder ?? t({ id: 'daily.notes.placeholder' })}
        onBeforeInput={handleBeforeInput}
        onInput={() => { handleInput(); refreshFormats(); }}
        onKeyUp={refreshFormats}
        onMouseUp={refreshFormats}
        onPaste={handleInput}
        onBlur={() => { void onBlurSave?.(readHtml()); }}
      />
      {maxLength && atLimit && (
        <p className="notes-modal__limit" role="status">
          {t({ id: 'daily.notes.limit' }, { max: maxLength })}
        </p>
      )}
    </div>
  );
});

export default NoteEditor;
